import { useEffect, useState } from "react";

import { initZusParams, useZusParams } from "..";
import { TRANSLATE_PREFIX } from "../../..";
import { useDatePicker } from "../../../../../../components/DatePicker";
import { useAssets, useChains } from "../../../../../../components/FetchConfig";
import GeneralBtn from "../../../../../../components/GeneralBtn";
import { NodeTypesEnumSingleSelection } from "../../../../../../components/GeneralSelection";
import MpTextField from "../../../../../../components/MpTextField";
import {
  Box,
  SelectChangeEvent,
} from "../../../../../../components/MuiGenerals";
import { useAlerting } from "../../../../../../hooks";
import { genField } from "../../../../../../utils/HelperComp";
import { customSx } from "../../../../../../utils/styling";
import { SweepDestGroupType } from "@wallet-manager/node-types/dist/src/postgres/const";
import useTabTranslation from "src/hooks/useTabTranslation";
import { FilterFields } from "../types/FilterFields";
import { TAB_TRANSLTE_PREFIX } from "../constants";
import { ApiParams } from "../types/ApiParams";
import ChainNameSingleSelection from "src/components/ContractSweep/ChainNameSingleSelection";
import CurrencySingleSelection from "src/components/ContractSweep/CurrencySingleSelection";

const initFields: FilterFields = {
  groupName: "",
  groupType: "",
  chainName: "",
  currency: "",
};

export function Filter() {
  const [fields, setFields] = useState<FilterFields>(initFields);
  const { t, tc, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });
  const zusParams = useZusParams();
  const { alerting } = useAlerting();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const DateObj = {
    ActiveStartTime: useDatePicker(),
    ActiveEndTime: useDatePicker(),
  };

  const getParams: () => ApiParams = () => {
    const { chainName, currency: assetName, ...rest } = fields;

    const { chain_type, chain_id } = selectChainByPieces({
      chain_name: fields.chainName,
    });
    const { ActiveStartTime, ActiveEndTime } = DateObj;
    const { start: enabledTimeFrom, end: enabledTimeTo } = ActiveStartTime;
    const { start: disabledTimeFrom, end: disabledTimeTo } = ActiveEndTime;

    return {
      ...initZusParams,
      chainId: chain_id,
      chainType: chain_type,
      assetName,
      enabledTimeFrom,
      enabledTimeTo,
      disabledTimeFrom,
      disabledTimeTo,
      ...rest,
    };
  };

  const apiParams = getParams();

  const onChange =
    (field: keyof FilterFields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>,
    ) => {
      const value = e.target.value;

      setFields((fields) => ({ ...fields, [field]: value }));
    };
  const onClear = (field: keyof FilterFields) => () => {
    setFields((fields) => ({ ...fields, [field]: "" }));
  };

  const onSearch = () => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.ActiveStartTime.clearDate();
    DateObj.ActiveEndTime.clearDate();
  };
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chainName,
  });
  useEffect(
    () => setFields((f) => ({ ...f, currency: "" })),
    [fields.chainName],
  );

  const F = genField({ t }, [
    ["activeStartTime", <DateObj.ActiveStartTime.Picker type="dateTime" />],
    ["activeEndTime", <DateObj.ActiveEndTime.Picker type="dateTime" />],
    [
      "chainName",
      <ChainNameSingleSelection
        fieldName={t("chainName")}
        value={fields.chainName}
        onChange={onChange("chainName")}
        onClear={onClear("chainName")}
      />,
    ],
    [
      "currency",
      <CurrencySingleSelection
        fieldName={t("currency")}
        value={fields.currency}
        onChange={onChange("currency")}
        onClear={onClear("currency")}
        chainName={fields.chainName}
      />,
    ],
    [
      "groupName",
      <MpTextField
        label={tc("phInputField", { fieldName: t("groupName") })}
        onChange={onChange("groupName")}
        value={fields.groupName}
      />,
    ],
    [
      "groupType",
      <NodeTypesEnumSingleSelection
        label={tc("phSelection", { fieldName: t("groupType") })}
        onChange={onChange("groupType")}
        value={fields.groupType}
        enumData={SweepDestGroupType}
        nameFn={(name) => translate(`enumSweepDestGroupType.${name}`)}
        clearSelect={onClear("groupType")}
      />,
    ],
  ]);
  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.activeStartTime}
        {F.activeEndTime}
        {F.chainName}
        {F.currency}
        {F.groupName}
        {F.groupType}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}
